import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, DotsHorizontalIcon } from '@radix-ui/react-icons';
import { type ButtonProps, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

const Pagination: React.FC<React.ComponentProps<'nav'>> = ({ className, ...props }) => {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
};
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
  onlyIcons?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<typeof Link>;

const PaginationLink = ({ className, isActive, size = 'icon', disabled, ...props }: PaginationLinkProps) => (
  <Link
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline-solid' : 'ghost',
        size,
      }),
      className,
      // ui-base is a near-black surface color in dark mode, so the variant's dark:text-ui-base is unreadable on the dark panel
      'dark:text-text-base dark:hover:text-text-base',
      isActive && 'bg-white dark:bg-neutral-950',
      disabled && 'pointer-events-none select-none opacity-50',
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({ className, onlyIcons, ...props }: React.ComponentProps<typeof PaginationLink>) => {
  const tCommon = useTranslations('common');
  const tPagination = useTranslations('pagination');

  return (
    <PaginationLink
      aria-label={tPagination('goToPreviousPage')}
      size="default"
      className={cn('gap-1', onlyIcons ? 'p-2' : 'pl-2.5', className)}
      {...props}
    >
      <ChevronLeftIcon className="icon-4" />
      {!onlyIcons && <span>{tCommon('previous')}</span>}
    </PaginationLink>
  );
};
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, onlyIcons, ...props }: React.ComponentProps<typeof PaginationLink>) => {
  const tCommon = useTranslations('common');
  const tPagination = useTranslations('pagination');

  return (
    <PaginationLink
      aria-label={tPagination('goToNextPage')}
      size="default"
      className={cn('gap-1', onlyIcons ? 'p-2' : 'pr-2.5', className)}
      {...props}
    >
      {!onlyIcons && <span>{tCommon('next')}</span>}
      <ChevronRightIcon className="icon-4" />
    </PaginationLink>
  );
};
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => {
  const tPagination = useTranslations('pagination');

  return (
    <span aria-hidden className={cn('flex h-9 w-9 items-center justify-center', className)} {...props}>
      <DotsHorizontalIcon className="icon-4" />
      <span className="sr-only">{tPagination('morePages')}</span>
    </span>
  );
};
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
